import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  FaArrowLeft, FaPaperPlane, FaPaperclip, FaFilePdf, FaImage,
  FaCheckDouble, FaClock, FaUser, FaSpinner, FaTimes
} from 'react-icons/fa';

export default function RequestConversation() {
  const { conversationId } = useParams();
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [attachment, setAttachment] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const isInitialLoad = useRef(true);
  const previousMessageCount = useRef(0);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchConversation();
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchConversation, 5000);
    return () => clearInterval(interval);
  }, [conversationId, user]);

  useEffect(() => {
    // Only scroll to bottom on initial load or when new messages are added
    if (isInitialLoad.current && messages.length > 0) {
      scrollToBottom();
      isInitialLoad.current = false;
      previousMessageCount.current = messages.length;
    } else if (messages.length > previousMessageCount.current) {
      // New message added
      scrollToBottom();
      previousMessageCount.current = messages.length;
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversation = async () => {
    try {
      const response = await axios.get(
        `${API}/requests/conversations/${conversationId}?user_id=${user.id}`
      );
      setConversation(response.data);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching conversation:', error);
      if (error.response?.status === 404) {
        toast.error(i18n.language === 'fr' ? 'Conversation non trouvée' : 'Conversation not found');
        navigate('/user/conversations');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10 MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(i18n.language === 'fr' ? 'Fichier trop volumineux (max 10 Mo)' : 'File too large (max 10 MB)');
      return;
    }

    setAttachment(file);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageContent.trim() && !attachment) {
      toast.error(i18n.language === 'fr' ? 'Veuillez écrire un message' : 'Please write a message');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('content', messageContent.trim() || 'Pièce jointe');
      if (attachment) {
        formData.append('attachment', attachment);
      }

      await axios.post(
        `${API}/requests/conversations/${conversationId}/message?user_id=${user.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setMessageContent('');
      setAttachment(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Refresh conversation to get new message
      await fetchConversation();
      
      toast.success(i18n.language === 'fr' ? 'Message envoyé' : 'Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.detail || (i18n.language === 'fr' ? 'Erreur lors de l\'envoi' : 'Error sending message'));
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOtherParty = () => {
    if (!conversation || !user) return 'Utilisateur';
    if (user.id === conversation.requester_id) {
      return conversation.supplier_name;
    }
    return conversation.requester_name;
  };

  const renderAttachment = (att) => {
    if (!att) return null;
    
    const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(att.type?.toLowerCase());
    const isPdf = att.type?.toLowerCase() === 'pdf';
    
    return (
      <a
        href={`${API}${att.url}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 mt-2 p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
      >
        {isImage && <FaImage className="text-green-500" />}
        {isPdf && <FaFilePdf className="text-red-500" />}
        {!isImage && !isPdf && <FaPaperclip className="text-gray-500" />}
        <span className="text-sm text-blue-600 underline">{att.filename}</span>
      </a>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#0A2540]" />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-4">
            {i18n.language === 'fr' ? 'Conversation non trouvée' : 'Conversation not found'}
          </p>
          <Link to="/user/conversations" className="text-[#635BFF] hover:underline">
            {i18n.language === 'fr' ? '← Retour aux conversations' : '← Back to conversations'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/user/conversations')}
            className="flex items-center gap-2"
            data-testid="back-to-conversations"
          >
            <FaArrowLeft />
            {i18n.language === 'fr' ? 'Retour' : 'Back'}
          </Button>
          
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#0A2540] line-clamp-1" data-testid="conversation-title">
              {conversation.request_title || 'Conversation'}
            </h1>
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <FaUser className="text-xs" />
              {i18n.language === 'fr' ? 'Conversation avec' : 'Conversation with'} <strong>{getOtherParty()}</strong>
            </p>
          </div>
        </div>

        {/* Messages */}
        <Card className="mb-4">
          <CardContent className="p-0">
            <div className="h-[500px] overflow-y-auto p-4 space-y-4" data-testid="messages-container">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {i18n.language === 'fr' 
                    ? 'Aucun message pour le moment' 
                    : 'No messages yet'}
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isOwnMessage = msg.sender_id === user.id;
                  
                  return (
                    <div
                      key={msg.id || index}
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${index}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                          isOwnMessage
                            ? 'bg-[#635BFF] text-white rounded-br-sm'
                            : 'bg-white border border-gray-200 rounded-bl-sm'
                        }`}
                      >
                        {!isOwnMessage && (
                          <p className={`text-xs font-medium mb-1 ${isOwnMessage ? 'text-white/80' : 'text-[#635BFF]'}`}>
                            {msg.sender_name}
                          </p>
                        )}
                        <p className={`text-sm ${isOwnMessage ? 'text-white' : 'text-gray-800'}`}>
                          {msg.content}
                        </p>
                        
                        {/* Attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mt-2">
                            {msg.attachments.map((att, attIdx) => renderAttachment(att))}
                          </div>
                        )}
                        
                        <div className={`flex items-center gap-1 mt-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                          <FaClock className={`text-xs ${isOwnMessage ? 'text-white/60' : 'text-gray-400'}`} />
                          <span className={`text-xs ${isOwnMessage ? 'text-white/60' : 'text-gray-400'}`}>
                            {formatDate(msg.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          </CardContent>
        </Card>

        {/* Message Input */}
        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleSendMessage} className="space-y-3">
              {/* Attachment Preview */}
              {attachment && (
                <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
                  <FaPaperclip className="text-gray-500" />
                  <span className="text-sm text-gray-700 flex-1 truncate">{attachment.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setAttachment(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-gray-500 hover:text-red-500"
                  >
                    <FaTimes />
                  </Button>
                </div>
              )}
              
              <div className="flex gap-2">
                <Textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder={i18n.language === 'fr' ? 'Écrivez votre message...' : 'Write your message...'}
                  className="flex-1 min-h-[80px] resize-none"
                  disabled={sending}
                  data-testid="message-input"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sending}
                    data-testid="attach-file-btn"
                  >
                    <FaPaperclip className="mr-2" />
                    {i18n.language === 'fr' ? 'Joindre' : 'Attach'}
                  </Button>
                </div>
                
                <Button
                  type="submit"
                  disabled={sending || (!messageContent.trim() && !attachment)}
                  className="bg-[#635BFF] hover:bg-[#5246E5] text-white"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <FaSpinner className="animate-spin mr-2" />
                  ) : (
                    <FaPaperPlane className="mr-2" />
                  )}
                  {i18n.language === 'fr' ? 'Envoyer' : 'Send'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
